import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ConnectionState } from 'livekit-client';
import { LivekitService } from '../../core/services/livekit.service';
import {
  ConfirmDialogData,
  MibBadgeComponent,
  MibButtonComponent,
  MibConfirmDialog,
  MibIconsModule,
} from '@medinbox/angular';
import { MibLogo } from '../../shared/mib-header/mib-logo';
import { Dialog } from '@angular/cdk/dialog';
import { distinctUntilChanged, filter, map, merge, Observable, of, switchMap } from 'rxjs';
import { AudioLevelIndicator } from '../../shared/audio-level-indicator/audio-level-indicator';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ResumeSessionChoice, ResumeSessionDialog } from '../../shared/resume-session-dialog';
import { ContextMode, SessionPayload } from '../../core/models/livekit.model';

@Component({
  selector: 'app-ingest',
  imports: [
    AudioLevelIndicator,
    MibBadgeComponent,
    MibButtonComponent,
    MibIconsModule,
    MibLogo,
    ReactiveFormsModule,
  ],
  templateUrl: './ingest.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Ingest implements AfterViewInit {
  private readonly livekit = inject(LivekitService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(Dialog);

  constructor() {
    this.destroyRef.onDestroy(() => this.stopPreview());
  }

  private readonly previewVideo = viewChild<ElementRef<HTMLVideoElement>>('previewVideo');
  private readonly role = 'ingest';

  readonly connectionState = this.livekit.connectionState;
  readonly localVideoTrack = this.livekit.localVideoTrack;
  readonly localAudioTrack = this.livekit.localAudioTrack;
  readonly ingestActive = signal(false);

  readonly isConnected = computed(() => this.connectionState() === ConnectionState.Connected);
  readonly isDisconnected = computed(() => this.connectionState() === ConnectionState.Disconnected);
  readonly manuallyTerminatedSession = signal(false);

  readonly voiceOutputEnabled = new FormControl(false);
  readonly maxWords = new FormControl(80, { nonNullable: true });

  private readonly previewStream = signal<MediaStream | null>(null);
  private readonly previewVideoTrack = signal<MediaStreamTrack | null>(null);
  private readonly previewAudioTrack = signal<MediaStreamTrack | null>(null);
  readonly mediaPermissionGranted = signal(false);
  readonly mediaPermissionError = signal<string | null>(null);

  readonly videoDevices = signal<MediaDeviceInfo[]>([]);
  readonly audioDevices = signal<MediaDeviceInfo[]>([]);
  readonly selectedVideoDeviceId = signal<string>('');
  readonly selectedAudioDeviceId = signal<string>('');

  readonly hasActiveVideo = computed(() => !!this.localVideoTrack() || !!this.previewVideoTrack());
  readonly hasActiveAudio = computed(() => !!this.localAudioTrack() || !!this.previewAudioTrack());
  readonly activeAudioTrack = computed(
    () => this.localAudioTrack()?.mediaStreamTrack ?? this.previewAudioTrack(),
  );

  private readonly activeVideo$ = toObservable(
    computed(() => ({
      livekitTrack: this.localVideoTrack(),
      previewTrack: this.previewVideoTrack(),
    })),
  );

  readonly connectionBadgeTheme = computed(() => {
    switch (this.connectionState()) {
      case ConnectionState.Connected:
        return 'success' as const;
      case ConnectionState.Reconnecting:
        return 'warning' as const;
      default:
        return 'danger' as const;
    }
  });

  readonly connectionLabel = computed(() => {
    switch (this.connectionState()) {
      case ConnectionState.Connected:
        return 'Connected';
      case ConnectionState.Connecting:
        return 'Connecting';
      case ConnectionState.Reconnecting:
        return 'Reconnecting';
      default:
        return 'Disconnected';
    }
  });

  ngAfterViewInit(): void {
    this.activeVideo$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ livekitTrack, previewTrack }) => {
        const el = this.previewVideo()?.nativeElement;
        if (!el) return;

        if (livekitTrack) {
          livekitTrack.attach(el);
        } else if (previewTrack) {
          el.srcObject = new MediaStream([previewTrack]);
        } else {
          el.srcObject = null;
        }
      });

    this.startPreview();
  }

  toggleIngest(): void {
    if (this.ingestActive()) {
      this.livekit.stopIngest();
      this.ingestActive.set(false);
    } else {
      this.livekit.startIngest();
      this.ingestActive.set(true);
    }
  }

  terminateSession(): void {
    this.dialog
      .open<ConfirmDialogData>(MibConfirmDialog, {
        data: {
          title: 'Terminate the session?',
          description: `You're about to terminate the session, all participants (panelist, cleanfeed and audience) will be disconnected.`,
          confirmLabel: 'Terminate',
          type: 'danger',
        },
      })
      .closed.pipe(
        filter((confirmed) => !!confirmed),
        switchMap(() => this.livekit.terminateSession(this.role)),
        switchMap(() => this.livekit.cleanupAfterDisconnection()),
      )
      .subscribe(() => this.manuallyTerminatedSession.set(true));
  }

  startSession(): void {
    const base: SessionPayload = {
      role: this.role,
      participantName: 'ingest-client',
      voiceEnabled: this.voiceOutputEnabled.getRawValue() ?? false,
      maxWords: this.maxWords.value,
    };

    // 1. Probe for a resumable context, 2. ask the user when relevant, then
    // 3. (re)connect with the chosen contextMode. That contextMode-bearing
    // /sessions call is the LAST one before joining, so the agent honours it —
    // and the popup is fully resolved before the ingest starts publishing.
    this.livekit
      .fetchSession(base)
      .pipe(
        switchMap((session) => this.resolveContextMode(session.hasExistingContext)),
        // `null` → the user dismissed the popup → do not start the session.
        filter((contextMode): contextMode is ContextMode => contextMode !== null),
        switchMap((contextMode) => this.livekit.connect({ ...base, contextMode })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        complete: () => {
          const videoDeviceId = this.selectedVideoDeviceId();
          const audioDeviceId = this.selectedAudioDeviceId();
          this.stopPreview();
          this.livekit.startIngest({ videoDeviceId, audioDeviceId });
          this.ingestActive.set(true);
          this.sendCurrentSettings();
          this.listenToSettingsChanges();
        },
      });
  }

  /**
   * Determines which `contextMode` to start the session with:
   * - no resumable context → `inherit` (default; joins directly, no popup);
   * - resumable context → ask the user: Continue (`inherit`) or new session (`reset`);
   *   returns `null` when the user dismisses the popup so the caller can abort.
   */
  private resolveContextMode(hasExistingContext: boolean): Observable<ContextMode | null> {
    if (!hasExistingContext) return of<ContextMode>('inherit');
    return this.dialog
      .open<ResumeSessionChoice>(ResumeSessionDialog)
      .closed.pipe(map((choice) => (choice ? (choice === 'new' ? 'reset' : 'inherit') : null)));
  }

  async switchVideoDevice(deviceId: string): Promise<void> {
    if (!this.mediaPermissionGranted()) return;
    this.selectedVideoDeviceId.set(deviceId);
    await this.restartPreview();
  }

  async switchAudioDevice(deviceId: string): Promise<void> {
    if (!this.mediaPermissionGranted()) return;
    this.selectedAudioDeviceId.set(deviceId);
    await this.restartPreview();
  }

  private async startPreview(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      this.previewStream.set(stream);
      this.previewVideoTrack.set(stream.getVideoTracks()[0] ?? null);
      this.previewAudioTrack.set(stream.getAudioTracks()[0] ?? null);
      this.mediaPermissionGranted.set(true);

      await this.enumerateDevices();

      const videoTrack = stream.getVideoTracks()[0];
      const audioTrack = stream.getAudioTracks()[0];
      if (videoTrack) this.selectedVideoDeviceId.set(videoTrack.getSettings().deviceId ?? '');
      if (audioTrack) this.selectedAudioDeviceId.set(audioTrack.getSettings().deviceId ?? '');
    } catch (err) {
      this.mediaPermissionError.set(
        err instanceof DOMException ? err.message : 'Failed to access camera/microphone',
      );
    }
  }

  private async restartPreview(): Promise<void> {
    const oldStream = this.previewStream();
    if (oldStream) {
      oldStream.getTracks().forEach((t) => t.stop());
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: this.selectedVideoDeviceId() } },
        audio: { deviceId: { exact: this.selectedAudioDeviceId() } },
      });
      this.previewStream.set(stream);
      this.previewVideoTrack.set(stream.getVideoTracks()[0] ?? null);
      this.previewAudioTrack.set(stream.getAudioTracks()[0] ?? null);
    } catch (err) {
      this.mediaPermissionError.set(
        err instanceof DOMException ? err.message : 'Failed to access camera/microphone',
      );
    }
  }

  private async enumerateDevices(): Promise<void> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    this.videoDevices.set(devices.filter((d) => d.kind === 'videoinput'));
    this.audioDevices.set(devices.filter((d) => d.kind === 'audioinput'));
  }

  private sendCurrentSettings(): void {
    this.livekit.sendSettings({
      voice: this.voiceOutputEnabled.value ?? false,
      maxWords: this.maxWords.value,
    });
  }

  private listenToSettingsChanges(): void {
    merge(
      this.voiceOutputEnabled.valueChanges.pipe(distinctUntilChanged()),
      this.maxWords.valueChanges.pipe(distinctUntilChanged()),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.sendCurrentSettings());
  }

  private stopPreview(): void {
    const stream = this.previewStream();
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
    }
    this.previewStream.set(null);
    this.previewVideoTrack.set(null);
    this.previewAudioTrack.set(null);
  }
}
